/* eslint-disable react-hooks/exhaustive-deps */
import * as React from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { IWaypointPostion } from './types';
import { IInfoChipsOverlays } from './DrawedWaypoint';
import {
  getCoordinatesFromPaths,
  createInfoWindow as createWaypointInfoWindow,
} from './WaypointService';
import {
  centerWaypointAtom,
  computedAreaAtom,
  coordinatesAtom,
  isDrawWaypointAtom,
  isEditModeAtom,
  isWaypointsOpenAtom,
} from './WaypointState';

interface IDrawWaypoint {
  map: google.maps.Map;
}

export default function DrawWaypoint(props: IDrawWaypoint): null {
  const isWaypointsOpen = useRecoilValue(isWaypointsOpenAtom);
  const setComputedArea = useSetRecoilState<number>(computedAreaAtom);
  const [drawManager, setDrawManager] = React.useState<google.maps.drawing.DrawingManager | null>(
    null
  );
  const [drawedPolygon, setDrawedPolygon] = React.useState<google.maps.Polygon | null>(null);
  const [coordinates, setCoordinates] = useRecoilState<IWaypointPostion[] | null>(coordinatesAtom);
  const isEditMode = useRecoilValue<boolean>(isEditModeAtom);
  const isDrawWaypoint = useRecoilValue<boolean>(isDrawWaypointAtom);
  const setCenter = useSetRecoilState<IWaypointPostion | null>(centerWaypointAtom);
  const { current: infoAreaOverlay } = React.useRef<IInfoChipsOverlays>({});

  const createInfoWindow = React.useCallback(
    (
      position: google.maps.LatLng,
      waypointID: number | string,
      createdPolygon: google.maps.Polygon
    ) => {
      const areaInfo = createWaypointInfoWindow(position, createdPolygon);
      infoAreaOverlay[waypointID] = areaInfo.newWindow;
      setComputedArea(areaInfo.area);

      infoAreaOverlay[waypointID].setMap(props.map);
    },
    [infoAreaOverlay, props.map]
  );

  React.useEffect(() => {
    if (props.map && isDrawWaypoint && isWaypointsOpen) {
      if (!drawManager) {
        setDrawManager(
          new google.maps.drawing.DrawingManager({
            drawingControl: false,
            drawingMode: !drawedPolygon ? google.maps.drawing.OverlayType.POLYGON : null,
            map: props.map,
            polygonOptions: {
              strokeColor: '#B50536',
              strokeWeight: 3,
              fillColor: '#FCD4DF',
              fillOpacity: 0.5,
              editable: true,
              clickable: false,
            },
          })
        );
      }
    }

    return () => {
      if (drawedPolygon && !isDrawWaypoint) {
        drawedPolygon.setMap(null);
        setDrawedPolygon(null);

        const removeChips = infoAreaOverlay;

        Object.keys(removeChips).forEach((remove) => {
          infoAreaOverlay[remove].setMap(null);
          delete infoAreaOverlay[remove];
        });
      }
      if (drawManager) {
        drawManager.setDrawingMode(null);
        setDrawManager(null);
      }
      if (!isDrawWaypoint) {
        setDrawedPolygon(null);
      }
    };
  }, [drawManager, isDrawWaypoint, props.map, isWaypointsOpen, drawedPolygon]);

  React.useEffect(() => {
    if (drawedPolygon) {
      const bounds = new google.maps.LatLngBounds();
      drawedPolygon
        .getPath()
        .getArray()
        .forEach((path) => {
          bounds.extend(path);
        });

      const center: IWaypointPostion = {
        latitude: bounds.getCenter().lat(),
        longitude: bounds.getCenter().lng(),
      };

      setCenter(center);
      props.map.fitBounds(bounds);
    }
  }, [drawedPolygon]);

  React.useEffect(() => {
    if (props.map && drawManager) {
      google.maps.event.addListenerOnce(drawManager, 'polygoncomplete', (polygon) => {
        if (drawManager) {
          drawManager.setOptions({
            polygonOptions: { editable: true },
          });
          drawManager.setDrawingMode(null);

          const rawCoordinates: google.maps.LatLng[] = polygon.getPath().getArray();
          polygon.setOptions({ clickable: true, editable: true, draggable: true });

          setCoordinates(getCoordinatesFromPaths(rawCoordinates));
          setDrawedPolygon(polygon);
        }
      });
    }
  });

  React.useEffect(() => {
    if (drawedPolygon) {
      const removeChips = infoAreaOverlay;
      if (drawManager) {
        drawManager.setDrawingMode(null);
      }

      Object.keys(removeChips).forEach((remove) => {
        infoAreaOverlay[remove].setMap(null);
        delete infoAreaOverlay[remove];
      });
      const bounds = new google.maps.LatLngBounds();
      drawedPolygon
        .getPath()
        .getArray()
        .forEach((path) => {
          bounds.extend(path);
        });

      const centerPolygon = new google.maps.LatLng(
        bounds.getCenter().lat(),
        bounds.getCenter().lng()
      );

      createInfoWindow(centerPolygon, 'drawed-waypoint', drawedPolygon);
    }
  }, [coordinates, drawedPolygon]);

  React.useEffect(() => {
    if ((drawedPolygon && isWaypointsOpen && isEditMode) || (drawedPolygon && isWaypointsOpen)) {
      google.maps.event.addListenerOnce(
        drawedPolygon,
        'mouseup',
        (_event: google.maps.PolyMouseEvent) => {
          const coords = drawedPolygon.getPath().getArray();
          // setRawCoordinates(coords);
          setCoordinates(getCoordinatesFromPaths(coords));
        }
      );
    }
  });

  return null;
}
